<script setup>
// Formulario UNICO de agendamento do SiteUP CRM (decisao 18 do kanban-layout-r3).
//
// Etapa 1 (opcao 1 da vistoria): o que e, quando, duracao, responsavel ja
// preenchido; local, agenda do Google e aviso no WhatsApp ficam recolhidos numa
// linha so. Etapa 2 (opcao 3) so aparece quando o aviso ao cliente esta ligado:
// resumo, previa da mensagem e a linha SOMENTE LEITURA do destino no Google.
//
// Por que somente leitura: nao existe opt-out de Google no backend
// (GoogleCalendar::PushEventJob#resolve_connection publica pela conexao do
// funil mesmo sem destino explicito) e o lembrete de 30 minutos e cron global
// para a equipe, sem configuracao por compromisso. Chave que nao muda nada e
// enfeite, entao as duas sairam. O unico controle de Google e o select
// "Agenda de destino" da linha recolhivel.
import {
  computed,
  nextTick,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';
import DatePicker from 'dashboard/components-next/datetime/DatePicker.vue';
import TimeSlotPicker from 'dashboard/components-next/datetime/TimeSlotPicker.vue';
import QuickActionButtons from 'dashboard/components/widgets/QuickActionButtons.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ContactAPI from 'dashboard/api/contacts';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';
import kanbanAPI from 'dashboard/api/kanban';
import {
  addDays,
  buildScheduledAt,
  CALENDAR_DESTINATION_DEFAULT,
  CALENDAR_DESTINATION_UNAVAILABLE,
  destinationStillWritable,
  explicitCalendarConnectionId,
  findConflict,
  formatMinutes,
  isCurrentGoogleCalendarAccessEvent,
  isScheduleReady,
  isoDate,
  nextBusinessOffset,
  parseTimeToMinutes,
  rejectConnectionsById,
  startOfDay,
  writableCalendarConnections,
} from './agendaHelpers';
import {
  buildSchedulePayload,
  contextLine,
  defaultResponsibleId,
  GOOGLE_DESTINATION,
  googleDestination,
  readDurationMin,
  shouldReview,
} from './scheduleFormHelpers';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  saveError: { type: String, default: '' },
  item: { type: Object, default: null },
  items: { type: Array, default: () => [] },
  funnelName: { type: String, default: '' },
  // Funil do compromisso. Sem ele a linha de destino do Google nao tem como
  // ser verdadeira, entao ela simplesmente nao aparece (estado 'unknown').
  funnelId: { type: [String, Number], default: null },
  agents: { type: Array, default: () => [] },
  // Pre-semente opcional. O aviso no WhatsApp e configuracao de CONTA
  // (kanban_config.appointment_waha), entao o proprio formulario le a config:
  // antes so a Agenda passava a prop e cartao e conversa ficavam mudos.
  wahaEnabled: { type: Boolean, default: false },
  locale: { type: String, default: 'pt-BR' },
  // Criação vinda da Central de Agendamento: pré-seleciona dia/horário
  // ({ date: Date|ISO, startMin: minutos|null }) sem entrar no modo edição.
  prefill: { type: Object, default: null },
  // Central: o painel fica numa camada altíssima; sem subir junto, este modal
  // abriria atrás dele.
  elevated: { type: Boolean, default: false },
  // Central: conflito só conta na agenda da corretora escolhida e trava o
  // envio até confirmar a sobreposição.
  guardConflicts: { type: Boolean, default: false },
  // Cartão do funil ligado ao compromisso. Habilita prévia e envio no WhatsApp.
  kanbanItemId: { type: [String, Number], default: null },
  // Contexto quando o formulário abre de um cartão ou de uma conversa.
  contactName: { type: String, default: '' },
  contactPhone: { type: String, default: '' },
  cardTitle: { type: String, default: '' },
  // Cartões do contato para a linha opcional "Ligar a um cartão" (conversa).
  cardOptions: { type: Array, default: () => [] },
  showCardLink: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'submit']);

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const store = useStore();
const route = useRoute();
const initialState = ref('');
const discardRequested = ref(false);
const connections = ref([]);
const connectionsStatus = ref('idle');
let connectionsGeneration = 0;
let connectionsUnmounted = false;
const googleCalendarAccessEvent = BUS_EVENTS.GOOGLE_CALENDAR_ACCESS_CHANGED;

const TIME_CHIPS = [
  { key: '9h', min: 540 },
  { key: '10h30', min: 630 },
  { key: '14h', min: 840 },
  { key: '16h30', min: 990 },
  { key: 'other', min: null },
];
const DURATION_CHIPS = [15, 30, 45, 60];

const today = startOfDay(new Date());
const form = ref({});
const submitted = ref(false);
const contactResults = ref([]);
const contactSearching = ref(false);
const advancedOpen = ref(false);
const step = ref('form');
const previewLoading = ref(false);
const previewFailed = ref(false);
const previewError = ref('');
const previewEnabled = ref(true);
const previewMessage = ref('');
// Verdade sobre o Google: 'unknown' ate o status do funil responder.
const funnelGoogleStatus = ref('unknown');
const funnelGoogleLabel = ref('');
const wahaAccountEnabled = ref(false);
const titleInput = ref(null);
let contactTimer = null;
let funnelGoogleGeneration = 0;

const isEditing = computed(() => Boolean(props.item));
const isRescheduling = computed(() => Boolean(props.item?.scheduled));
const isCard = computed(() => props.item?.kind === 'kanban_item');
// Sem contexto de router (chip da conversa montado em teste ou fora de rota)
// o useRoute devolve undefined; o formulario nao pode quebrar por causa disso.
const accountId = computed(() => route?.params?.accountId);
const currentUserId = computed(() => store.getters.getCurrentUser?.id);
const connectionsContextKey = () =>
  `${accountId.value || ''}|${currentUserId.value || ''}`;
const writableDestinations = computed(() =>
  writableCalendarConnections(connections.value)
);
// Antes o seletor dependia de uma prop que só a Agenda ligava. Agora aparece em
// qualquer pai, desde que exista conexão Google gravável.
const showDestinationSelect = computed(() => !isEditing.value);
const defaultDestinationLabel = computed(() =>
  props.funnelName
    ? t('KANBAN.AGENDA_BOARD.DESTINATION_FUNNEL_DEFAULT', {
        funnel: props.funnelName,
      })
    : t('KANBAN.AGENDA_BOARD.DESTINATION_DEFAULT')
);
const destinationTypeLabel = scope => {
  if (scope === 'personal')
    return t('KANBAN.AGENDA_BOARD.DESTINATION_PERSONAL');
  if (scope === 'account') return t('KANBAN.AGENDA_BOARD.DESTINATION_SHARED');
  if (scope === 'funnel') return t('KANBAN.AGENDA_BOARD.DESTINATION_FUNNEL');
  return '';
};
const destinationOptionLabel = connection => {
  const type = destinationTypeLabel(connection.connection_scope);
  const email = connection.google_email;
  if (email && type) {
    return t('KANBAN.AGENDA_BOARD.DESTINATION_ORIGIN', { email, type });
  }
  if (type) return t('KANBAN.AGENDA_BOARD.DESTINATION_ORIGIN_SCOPE', { type });
  return email || '';
};
const originDestinationLabel = computed(() => {
  const item = props.item;
  if (!item) return '';
  const type = destinationTypeLabel(
    item.connectionScope || item.connection_scope
  );
  const email = item.googleEmail || item.google_email;
  if (email && type) {
    return t('KANBAN.AGENDA_BOARD.DESTINATION_ORIGIN', { email, type });
  }
  if (type) return t('KANBAN.AGENDA_BOARD.DESTINATION_ORIGIN_SCOPE', { type });
  return '';
});
const destinationUnavailable = computed(
  () => form.value.destinationId === CALENDAR_DESTINATION_UNAVAILABLE
);
const hasExplicitDestination = computed(() =>
  Boolean(explicitCalendarConnectionId(form.value.destinationId))
);
const selectedDestination = computed(
  () =>
    writableDestinations.value.find(
      connection => String(connection.id) === String(form.value.destinationId)
    ) || null
);
const destinationBlocksSubmit = computed(() => {
  if (!showDestinationSelect.value) return false;
  if (destinationUnavailable.value) return true;
  if (!hasExplicitDestination.value) return false;
  if (
    connectionsStatus.value === 'loading' ||
    connectionsStatus.value === 'error'
  ) {
    return true;
  }
  return !destinationStillWritable(form.value.destinationId, connections.value);
});
// Só entra na linha recolhida quando existe destino gravável de verdade.
const showDestinationPicker = computed(
  () =>
    showDestinationSelect.value &&
    (destinationUnavailable.value ||
      (connectionsStatus.value === 'ready' &&
        writableDestinations.value.length))
);
const rememberedDestination = ref(null);
const rememberedDestinationId = ref('');

const clearConnections = () => {
  connections.value = [];
  connectionsStatus.value = 'idle';
};

const rememberExplicitDestination = destinationId => {
  const connectionId = explicitCalendarConnectionId(destinationId);
  if (!connectionId) return;
  rememberedDestinationId.value = String(connectionId);
};

const applyLoadedDestinations = rows => {
  connections.value = rows;
  connectionsStatus.value = 'ready';
  const selected = form.value.destinationId;
  if (
    selected === CALENDAR_DESTINATION_UNAVAILABLE &&
    destinationStillWritable(rememberedDestinationId.value, rows)
  ) {
    form.value.destinationId = rememberedDestinationId.value;
    return;
  }
  if (
    selected &&
    selected !== CALENDAR_DESTINATION_DEFAULT &&
    selected !== CALENDAR_DESTINATION_UNAVAILABLE &&
    !destinationStillWritable(selected, rows)
  ) {
    rememberExplicitDestination(selected);
    form.value.destinationId = CALENDAR_DESTINATION_UNAVAILABLE;
  }
};

const loadConnections = async ({
  resetDestination = false,
  preserveExisting = false,
} = {}) => {
  if (!showDestinationSelect.value) {
    connectionsGeneration += 1;
    clearConnections();
    return;
  }
  connectionsGeneration += 1;
  const generation = connectionsGeneration;
  const contextKey = connectionsContextKey();
  if (!preserveExisting) {
    connections.value = [];
    connectionsStatus.value = 'loading';
  }
  if (resetDestination && form.value) {
    form.value.destinationId = CALENDAR_DESTINATION_DEFAULT;
    rememberedDestinationId.value = '';
    rememberedDestination.value = null;
  }
  try {
    const { data } = await GoogleCalendarAPI.listConnections();
    if (
      connectionsUnmounted ||
      generation !== connectionsGeneration ||
      contextKey !== connectionsContextKey()
    ) {
      return;
    }
    applyLoadedDestinations(Array.isArray(data) ? data : []);
  } catch (error) {
    if (
      connectionsUnmounted ||
      generation !== connectionsGeneration ||
      contextKey !== connectionsContextKey()
    ) {
      return;
    }
    if (
      preserveExisting &&
      ![401, 403, 404].includes(error?.response?.status)
    ) {
      connectionsStatus.value = connections.value.length ? 'ready' : 'error';
      if (
        hasExplicitDestination.value &&
        !destinationStillWritable(form.value.destinationId, connections.value)
      ) {
        rememberExplicitDestination(form.value.destinationId);
        form.value.destinationId = CALENDAR_DESTINATION_UNAVAILABLE;
      }
      return;
    }
    connections.value = [];
    connectionsStatus.value = 'error';
    if (hasExplicitDestination.value) {
      rememberExplicitDestination(form.value.destinationId);
      form.value.destinationId = CALENDAR_DESTINATION_UNAVAILABLE;
    }
  }
};

const onGoogleCalendarAccessChanged = event => {
  if (
    !isCurrentGoogleCalendarAccessEvent(event, {
      accountId: accountId.value,
      userId: currentUserId.value,
    })
  ) {
    return;
  }
  connectionsGeneration += 1;
  if (!props.modelValue || !showDestinationSelect.value) return;
  connections.value = rejectConnectionsById(
    connections.value,
    event.connection_id
  );
  if (String(form.value.destinationId) === String(event.connection_id)) {
    rememberExplicitDestination(form.value.destinationId);
    form.value.destinationId = CALENDAR_DESTINATION_UNAVAILABLE;
  }
  loadConnections({ preserveExisting: true });
};

const revalidateDestinationsWhenActive = () => {
  if (connectionsUnmounted || document.visibilityState === 'hidden') return;
  if (!props.modelValue || !showDestinationSelect.value) return;
  loadConnections({ preserveExisting: true });
};

const cardOwnerLabel = computed(() => {
  if (!props.item?.responsibleId)
    return t('KANBAN.AGENDA_BOARD.CARD_UNASSIGNED');
  return (
    props.item.responsibleName ||
    props.item.responsible?.name ||
    props.agents.find(
      agent => Number(agent.id) === Number(props.item.responsibleId)
    )?.name ||
    t('KANBAN.AGENDA_BOARD.CARD_OWNER_ID', { id: props.item.responsibleId })
  );
});
const responsibleOptions = computed(() => {
  const list = Array.isArray(props.agents) ? [...props.agents] : [];
  const push = (id, name) => {
    if (id == null) return;
    if (list.some(agent => Number(agent.id) === Number(id))) return;
    list.push({ id, name });
  };
  push(props.item?.responsibleId, cardOwnerLabel.value);
  push(currentUserId.value, store.getters.getCurrentUser?.name || '');
  return list;
});
const responsibleName = computed(
  () =>
    responsibleOptions.value.find(
      agent => Number(agent.id) === Number(form.value.responsibleId)
    )?.name || ''
);

const resolvedContactName = computed(
  () => props.contactName || props.item?.contact?.name || ''
);
const resolvedCardTitle = computed(
  () => props.cardTitle || (isCard.value ? props.item?.title || '' : '')
);
const dialogTitle = computed(() =>
  isRescheduling.value
    ? t('KANBAN.AGENDA_BOARD.RESCHEDULE')
    : t('KANBAN.AGENDA_BOARD.NEW')
);
// Linha de contexto do topo. Substitui a caixa "Contato / origem / destino".
const subtitle = computed(() =>
  contextLine({
    contactLabel: resolvedContactName.value
      ? t('KANBAN.AGENDA_BOARD.CONTEXT_CONTACT', {
          name: resolvedContactName.value,
        })
      : '',
    cardLabel: resolvedCardTitle.value
      ? t('KANBAN.AGENDA_BOARD.CONTEXT_CARD', {
          title: resolvedCardTitle.value,
        })
      : '',
    funnelName: props.funnelName,
  })
);
const sourceLabel = computed(() => {
  if (props.item?.source === 'google')
    return t('KANBAN.AGENDA_BOARD.SOURCE_GOOGLE');
  if (props.item?.kind === 'kanban_item')
    return t('KANBAN.AGENDA_BOARD.SOURCE_KANBAN');
  return t('KANBAN.AGENDA_BOARD.SOURCE_MANUAL');
});
const originSummary = computed(() =>
  contextLine({
    contactLabel: originDestinationLabel.value,
    cardLabel: sourceLabel.value,
  })
);

const resolvedKanbanItemId = computed(
  () => props.kanbanItemId || props.item?.kanban?.item_id || null
);
const resolvedPhone = computed(
  () =>
    props.contactPhone ||
    props.item?.contact?.phone_number ||
    props.item?.contact?.phone ||
    ''
);
// appointment_waha e configuracao de CONTA. A prop so pre-semeia.
const effectiveWahaEnabled = computed(
  () => props.wahaEnabled || wahaAccountEnabled.value
);
// Cartao em vigor: o ligado na linha opcional tem prioridade sobre o herdado.
const activeKanbanItemId = computed(
  () => form.value.linkedCardId || resolvedKanbanItemId.value || null
);
// Aviso no WhatsApp: precisa do toggle da conta, de telefone e de um cartão
// (a prévia e o envio são endpoints do cartão).
const showWahaToggle = computed(
  () =>
    effectiveWahaEnabled.value &&
    Boolean(resolvedPhone.value) &&
    Boolean(activeKanbanItemId.value)
);
// Conversa sem cartao: o aviso nao pode ser oferecido, entao a tela explica o
// motivo em vez de esconder (decisao 3). A busca "Ligar a um cartao" fica logo
// abaixo; ao ligar um cartao o aviso passa a ser oferecido.
const showWahaNeedsCard = computed(
  () =>
    effectiveWahaEnabled.value &&
    Boolean(resolvedPhone.value) &&
    !activeKanbanItemId.value &&
    props.showCardLink
);
// Regra de quando a conferência entra no caminho.
const needsReview = computed(() =>
  shouldReview({
    wahaEnabled: effectiveWahaEnabled.value,
    hasPhone: Boolean(resolvedPhone.value),
    hasKanbanItem: Boolean(activeKanbanItemId.value),
    sendWhats: form.value.sendWhats,
  })
);

// Linha somente leitura do Google. Nao ha opt-out no backend, entao aqui nao
// existe chave: a tela conta para onde o compromisso vai de verdade.
const googleDestinationState = computed(() =>
  googleDestination({
    explicitLabel: selectedDestination.value
      ? destinationOptionLabel(selectedDestination.value)
      : '',
    funnelStatus: funnelGoogleStatus.value,
    funnelLabel: funnelGoogleLabel.value,
  })
);
const googleDestinationLine = computed(() => {
  const state = googleDestinationState.value;
  if (state.mode === GOOGLE_DESTINATION.GOOGLE) {
    return t('KANBAN.AGENDA_BOARD.GOOGLE_DESTINATION_LINE', {
      calendar: state.label || defaultDestinationLabel.value,
    });
  }
  if (state.mode === GOOGLE_DESTINATION.LOCAL) {
    return t('KANBAN.AGENDA_BOARD.GOOGLE_DESTINATION_NONE');
  }
  return '';
});
const googleDestinationGoes = computed(
  () => googleDestinationState.value.mode === GOOGLE_DESTINATION.GOOGLE
);

// Status do Google do funil. E o que o backend usa por padrao quando nenhum
// destino explicito foi escolhido.
const loadFunnelGoogle = async () => {
  funnelGoogleGeneration += 1;
  const generation = funnelGoogleGeneration;
  const funnelId = props.funnelId;
  if (!funnelId) {
    funnelGoogleStatus.value = 'unknown';
    funnelGoogleLabel.value = '';
    return;
  }
  funnelGoogleStatus.value = 'unknown';
  try {
    const { data } = await GoogleCalendarAPI.status(funnelId);
    if (connectionsUnmounted || generation !== funnelGoogleGeneration) return;
    // 'connected' sozinho nao basta: conexao com erro de token nao publica.
    const usable = data?.connected === true && data?.status !== 'error';
    if (data?.sync_mode === 'disabled') {
      funnelGoogleStatus.value = 'none';
      funnelGoogleLabel.value = '';
      return;
    }
    funnelGoogleStatus.value = usable ? 'connected' : 'none';
    if (!usable) {
      funnelGoogleLabel.value = '';
      return;
    }
    funnelGoogleLabel.value =
      data.sync_mode === 'account_default'
        ? t('KANBAN.AGENDA_BOARD.CALENDARS.ACCOUNT_DEFAULT_MODE')
        : data.google_email || data.calendar_id || props.funnelName;
  } catch (error) {
    if (connectionsUnmounted || generation !== funnelGoogleGeneration) return;
    // Sem resposta nao da para afirmar nada; a linha some.
    funnelGoogleStatus.value = 'unknown';
    funnelGoogleLabel.value = '';
  }
};

const loadWahaConfig = async () => {
  if (props.wahaEnabled) return;
  try {
    const config = await kanbanAPI.getAppointmentConfig();
    if (connectionsUnmounted) return;
    wahaAccountEnabled.value = config?.enabled === true;
  } catch (error) {
    if (connectionsUnmounted) return;
    wahaAccountEnabled.value = false;
  }
};

const quickActionContactId = computed(() => props.item?.contact?.id || null);
const quickActionConversationId = computed(
  () => props.item?.contact?.latest_conversation_display_id || null
);
const quickActionKanbanItemId = computed(
  () => props.item?.kanban?.item_id || null
);
const showQuickActions = computed(
  () =>
    isWorkspaceV2.value &&
    isEditing.value &&
    quickActionContactId.value &&
    quickActionKanbanItemId.value
);

const weekdayShort = date =>
  date.toLocaleDateString(props.locale, { weekday: 'short' }).replace('.', '');

const chip3 = computed(() => {
  const offset = nextBusinessOffset(2, today);
  const date = addDays(today, offset);
  return {
    key: `${weekdayShort(date)} ${date.getDate()}`,
    offset,
  };
});

const dayChips = computed(() => [
  { key: 'today', offset: 0, label: t('KANBAN.AGENDA_BOARD.TODAY') },
  { key: 'tomorrow', offset: 1, label: t('KANBAN.AGENDA_BOARD.TOMORROW') },
  { key: 'chip3', offset: chip3.value.offset, label: chip3.value.key },
  { key: 'other', offset: null, label: t('KANBAN.AGENDA_BOARD.OTHER_DATE') },
]);

const resolvedOffset = computed(() => {
  const chip = dayChips.value.find(entry => entry.key === form.value.day);
  if (chip && chip.offset !== null) return chip.offset;
  if (!form.value.customDate) return null;
  const [year, month, day] = form.value.customDate.split('-').map(Number);
  const target = startOfDay(new Date(year, month - 1, day));
  return Math.round((target.getTime() - today.getTime()) / 86400000);
});

const resolvedStart = computed(() => {
  const chip = TIME_CHIPS.find(entry => entry.key === form.value.time);
  if (chip && chip.min !== null) return chip.min;
  return parseTimeToMinutes(form.value.customTime);
});

const resolvedDayDate = computed(() =>
  resolvedOffset.value == null ? null : addDays(today, resolvedOffset.value)
);

const scheduleReady = computed(() =>
  isScheduleReady({
    dayResolved: resolvedOffset.value,
    startResolved: resolvedStart.value,
    responsibleId: form.value.responsibleId,
    // O responsável já nasce preenchido; deixa de ser uma decisão manual.
    requireResponsible: false,
  })
);

const probeItem = computed(() => {
  if (!scheduleReady.value || !resolvedDayDate.value) return null;
  return {
    id: props.item?.id || -1,
    kind: props.item?.kind || 'appointment',
    dayKey: isoDate(resolvedDayDate.value),
    startMin: resolvedStart.value,
    durationMin: form.value.duration,
    status: 'scheduled',
  };
});

const conflictPool = computed(() => {
  if (!props.guardConflicts || !form.value.responsibleId) return props.items;
  return props.items.filter(
    item =>
      item.responsibleId == null ||
      Number(item.responsibleId) === Number(form.value.responsibleId)
  );
});

const scheduleConflict = computed(() =>
  probeItem.value ? findConflict(probeItem.value, conflictPool.value) : null
);

const overrideConflict = ref(false);
watch(scheduleConflict, () => {
  overrideConflict.value = false;
});

const initialTimeKey = (item, timeChip) => {
  if (item && !item.scheduled) return null;
  if (timeChip) return timeChip.key;
  return item ? 'other' : null;
};

const resetForm = () => {
  submitted.value = false;
  discardRequested.value = false;
  advancedOpen.value = false;
  step.value = 'form';
  previewFailed.value = false;
  previewError.value = '';
  previewMessage.value = '';
  previewEnabled.value = true;
  contactResults.value = [];
  const item = props.item;
  const startMin = item?.startMin;
  const timeChip = TIME_CHIPS.find(entry => entry.min === startMin);
  let dayKey = 'today';
  let customDate = '';
  if (item?.scheduled) {
    const offset = Math.round(
      (startOfDay(item.scheduled).getTime() - today.getTime()) / 86400000
    );
    const chip = dayChips.value.find(entry => entry.offset === offset);
    dayKey = chip ? chip.key : 'other';
    customDate = dayKey === 'other' ? isoDate(item.scheduled) : '';
  }
  form.value = {
    title: item?.title || '',
    day: dayKey,
    customDate,
    time: initialTimeKey(item, timeChip),
    customTime: !timeChip && item?.scheduled ? formatMinutes(startMin) : '',
    duration: readDurationMin(item) || item?.durationMin || 30,
    responsibleId: defaultResponsibleId({
      itemResponsibleId: item?.responsibleId ?? null,
      currentUserId: currentUserId.value ?? null,
    }),
    location: item?.location || '',
    sendWhats: true,
    contactId: item?.contact?.id || null,
    contactName: resolvedContactName.value,
    linkedCardId: props.kanbanItemId || null,
    destinationId: CALENDAR_DESTINATION_DEFAULT,
  };
  if (!item && props.prefill) {
    const prefillDate = props.prefill.date
      ? new Date(props.prefill.date)
      : null;
    if (prefillDate && !Number.isNaN(prefillDate.getTime())) {
      const offset = Math.round(
        (startOfDay(prefillDate).getTime() - today.getTime()) / 86400000
      );
      const chip = dayChips.value.find(entry => entry.offset === offset);
      form.value.day = chip ? chip.key : 'other';
      form.value.customDate = chip ? '' : isoDate(prefillDate);
    }
    if (props.prefill.startMin != null) {
      const timeChipPrefill = TIME_CHIPS.find(
        entry => entry.min === props.prefill.startMin
      );
      form.value.time = timeChipPrefill ? timeChipPrefill.key : 'other';
      form.value.customTime = timeChipPrefill
        ? ''
        : formatMinutes(props.prefill.startMin);
    }
  }
  initialState.value = JSON.stringify(form.value);
};

watch(
  () => props.modelValue,
  open => {
    if (!open) {
      connectionsGeneration += 1;
      funnelGoogleGeneration += 1;
      clearConnections();
      rememberedDestinationId.value = '';
      rememberedDestination.value = null;
      return;
    }
    resetForm();
    if (showDestinationSelect.value) loadConnections();
    else {
      connectionsGeneration += 1;
      clearConnections();
    }
    loadFunnelGoogle();
    loadWahaConfig();
    // Foco inicial em "O que e" (vistoria agA). Sem isto o operador abria o
    // formulario e tinha de clicar antes de escrever.
    nextTick(() => titleInput.value?.focus());
  },
  { immediate: true }
);

// Trocar de funil sem fechar o formulario muda o destino do Google.
watch(
  () => props.funnelId,
  () => {
    if (props.modelValue) loadFunnelGoogle();
  }
);

watch([accountId, currentUserId], () => {
  connectionsGeneration += 1;
  connections.value = [];
  connectionsStatus.value = 'idle';
  rememberedDestinationId.value = '';
  rememberedDestination.value = null;
  if (form.value) form.value.destinationId = CALENDAR_DESTINATION_DEFAULT;
  if (props.modelValue && showDestinationSelect.value) {
    loadConnections({ resetDestination: true });
  }
});

watch(selectedDestination, connection => {
  if (connection) rememberedDestination.value = connection;
});

// Busca de contato só quando o formulário abre sem contexto (pela Agenda).
const contactIsFixed = computed(() =>
  Boolean(props.item || resolvedContactName.value)
);

watch(
  () => form.value.contactName,
  query => {
    if (contactTimer) clearTimeout(contactTimer);
    if (contactIsFixed.value) return;
    if (form.value.contactId) return;
    contactTimer = setTimeout(async () => {
      const term = String(query || '').trim();
      if (term.length < 2) {
        contactResults.value = [];
        return;
      }
      contactSearching.value = true;
      try {
        const { data } = await ContactAPI.search(term, 1);
        contactResults.value = (data?.payload || []).slice(0, 8);
      } catch (error) {
        contactResults.value = [];
      } finally {
        contactSearching.value = false;
      }
    }, 300);
  }
);

const pickContact = contact => {
  form.value.contactId = contact.id;
  form.value.contactName = contact.name;
  contactResults.value = [];
};

const onQuickActionExecuted = () => {
  emitter.emit('kanban.item.updated', {
    id: quickActionKanbanItemId.value,
  });
};

onMounted(() => {
  window.addEventListener('focus', revalidateDestinationsWhenActive);
  document.addEventListener(
    'visibilitychange',
    revalidateDestinationsWhenActive
  );
  emitter.on(googleCalendarAccessEvent, onGoogleCalendarAccessChanged);
  emitter.on(
    BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
    revalidateDestinationsWhenActive
  );
});

onBeforeUnmount(() => {
  connectionsUnmounted = true;
  connectionsGeneration += 1;
  clearTimeout(contactTimer);
  window.removeEventListener('focus', revalidateDestinationsWhenActive);
  document.removeEventListener(
    'visibilitychange',
    revalidateDestinationsWhenActive
  );
  emitter.off(googleCalendarAccessEvent, onGoogleCalendarAccessChanged);
  emitter.off(
    BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
    revalidateDestinationsWhenActive
  );
});

const close = () => {
  if (props.saving) return;
  if (
    isWorkspaceV2.value &&
    initialState.value !== JSON.stringify(form.value)
  ) {
    discardRequested.value = true;
    return;
  }
  emit('update:modelValue', false);
};

const dayChipLabel = chip => {
  if (chip.key !== 'other' || !form.value.customDate) return chip.label;
  const [year, month, day] = form.value.customDate.split('-');
  return `${day}/${month}/${String(year).slice(2)}`;
};

const timeChipLabel = chip => {
  if (chip.key !== 'other') return chip.key;
  return form.value.customTime || t('KANBAN.AGENDA_BOARD.OTHER_TIME');
};

const durationLabel = duration => `${duration}m`;

const scheduledAtValue = computed(() =>
  resolvedDayDate.value == null || resolvedStart.value == null
    ? ''
    : buildScheduledAt(resolvedDayDate.value, resolvedStart.value)
);

const summaryLabel = computed(() => {
  if (!resolvedDayDate.value || resolvedStart.value == null) return '';
  const day = resolvedDayDate.value.toLocaleDateString(props.locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
  const end = resolvedStart.value + Number(form.value.duration || 30);
  return `${day} · ${formatMinutes(resolvedStart.value)} – ${formatMinutes(end)}`;
});

const payloadFor = () =>
  buildSchedulePayload({
    item: props.item,
    scheduledAt: scheduledAtValue.value,
    durationMin: form.value.duration,
    title: form.value.title,
    responsibleId: form.value.responsibleId,
    responsibleName: responsibleName.value,
    location: form.value.location,
    contactId: form.value.contactId,
    kanbanItemId: activeKanbanItemId.value,
    sendWhats: needsReview.value && step.value === 'review',
    googleCalendarConnectionId: isEditing.value
      ? null
      : explicitCalendarConnectionId(form.value.destinationId),
  });

const refreshPreview = async () => {
  const cardId = activeKanbanItemId.value;
  if (!cardId) return;
  previewLoading.value = true;
  previewFailed.value = false;
  previewError.value = '';
  try {
    const { data } = await kanbanAPI.previewAppointment(cardId, {
      scheduled_at: scheduledAtValue.value,
      duration_minutes: form.value.duration,
      location: form.value.location,
      responsible_name: responsibleName.value,
    });
    previewEnabled.value = data.enabled !== false;
    previewMessage.value = data.message || '';
  } catch (e) {
    previewFailed.value = true;
    previewError.value = e?.response?.data?.error || e?.message || '';
    previewMessage.value = '';
  } finally {
    previewLoading.value = false;
  }
};

const blocked = computed(
  () =>
    destinationBlocksSubmit.value ||
    (props.guardConflicts &&
      Boolean(scheduleConflict.value) &&
      !overrideConflict.value)
);

const goToReview = () => {
  submitted.value = true;
  if (!scheduleReady.value || blocked.value) return;
  step.value = 'review';
  refreshPreview();
};

const backToForm = () => {
  step.value = 'form';
  previewFailed.value = false;
  previewError.value = '';
};

const emitSubmit = () => {
  if (props.saving) return;
  submitted.value = true;
  if (!scheduleReady.value || blocked.value) return;
  emit('submit', payloadFor());
};

// Botão principal da etapa 1: "Continuar" quando o aviso ao cliente está
// ligado, senão grava direto.
const primaryAction = () => {
  if (needsReview.value) {
    goToReview();
    return;
  }
  emitSubmit();
};

// "Salvar sem avisar" na conferência: mesma gravação, sem mensagem.
const saveWithoutNotice = () => {
  if (props.saving) return;
  const payload = payloadFor();
  payload.sendWhats = false;
  payload.confirmation = null;
  emit('submit', payload);
};

defineExpose({ refreshPreview });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[2px]"
      :class="elevated ? 'z-[2147482500]' : 'z-50'"
      role="dialog"
      aria-modal="true"
      :aria-label="dialogTitle"
      @keydown.esc.stop="close"
      @click.self="close"
    >
      <div
        class="flex max-h-[92vh] w-[500px] max-w-[94vw] flex-col rounded-3xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="m-0 text-[17px] font-medium text-n-slate-12">
              {{
                step === 'review' ? summaryLabel || dialogTitle : dialogTitle
              }}
            </h2>
            <p
              v-if="subtitle"
              class="m-0 mt-0.5 text-[12.5px] text-n-slate-11"
              data-testid="schedule-context"
            >
              {{ subtitle }}
            </p>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="t('KANBAN.AGENDA_BOARD.CLOSE')"
            @click="close"
          >
            <span class="i-ri-close-line size-5" aria-hidden="true" />
          </button>
        </div>

        <!-- Etapa 1: o formulário enxuto -->
        <div
          v-if="step === 'form'"
          class="mt-4 flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto pr-1"
        >
          <section
            v-if="showQuickActions"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
            :aria-label="t('KANBAN.QUICK_ACTIONS')"
          >
            <h3 class="mb-3 text-sm font-medium text-n-slate-12">
              {{ t('KANBAN.QUICK_ACTIONS') }}
            </h3>
            <QuickActionButtons
              :key="`${quickActionKanbanItemId}:${quickActionContactId}`"
              :contact-id="quickActionContactId"
              :conversation-id="quickActionConversationId"
              :kanban-item-id="quickActionKanbanItemId"
              :appointment-id="item.kind === 'appointment' ? item.id : null"
              context="kanban_agenda"
              @executed="onQuickActionExecuted"
            />
          </section>

          <!-- Contato: busca só quando o formulário abre sem contexto. -->
          <div v-if="!contactIsFixed">
            <span
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.CONTACT') }}
            </span>
            <div class="relative">
              <input
                v-model="form.contactName"
                :aria-label="t('KANBAN.AGENDA_BOARD.CONTACT')"
                type="text"
                :placeholder="t('KANBAN.AGENDA_BOARD.SEARCH_CONTACT')"
                class="!h-10 !mb-0 !px-2.5 !py-0 w-full rounded-xl border border-n-weak bg-n-solid-2 text-[13px] font-medium text-n-slate-12"
                @input="form.contactId = null"
              />
              <div
                v-if="contactResults.length || contactSearching"
                class="absolute left-0 right-0 top-full z-20 mt-1 rounded-xl border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
              >
                <p
                  v-if="contactSearching"
                  class="m-0 px-2 py-1 text-[11px] text-n-slate-10"
                >
                  {{ t('KANBAN.AGENDA_BOARD.SEARCHING') }}
                </p>
                <button
                  v-for="contact in contactResults"
                  :key="contact.id"
                  type="button"
                  class="flex w-full items-center rounded-lg px-2 py-1.5 text-left text-[12px] hover:bg-n-solid-2"
                  @click="pickContact(contact)"
                >
                  {{ contact.name }}
                </button>
              </div>
            </div>
          </div>

          <div>
            <label
              for="agenda-title"
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.WHAT') }}
            </label>
            <!-- Nao existe <form> aqui, entao Enter nao envia. O prevent deixa
                 isso explicito e protege de um wrapper futuro. -->
            <input
              id="agenda-title"
              ref="titleInput"
              v-model="form.title"
              type="text"
              :placeholder="t('KANBAN.AGENDA_BOARD.TITLE_PLACEHOLDER')"
              class="!h-10 !mb-0 !px-3.5 !py-0 w-full shrink-0 rounded-xl border border-n-weak bg-n-solid-2 text-[14px] font-medium text-n-slate-12 placeholder:text-n-slate-10"
              @keydown.enter.prevent
            />
          </div>

          <div>
            <span
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.WHEN') }}
            </span>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="chip in dayChips"
                :key="chip.key"
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold"
                :class="
                  form.day === chip.key
                    ? 'bg-n-slate-12 text-n-solid-1'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="form.day = chip.key"
              >
                {{ dayChipLabel(chip) }}
              </button>
            </div>
            <div v-if="form.day === 'other'" class="mt-2">
              <DatePicker v-model="form.customDate" />
            </div>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="chip in TIME_CHIPS"
                :key="chip.key"
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold tabular-nums"
                :class="
                  form.time === chip.key
                    ? 'bg-n-blue-9 text-white'
                    : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                "
                @click="form.time = chip.key"
              >
                {{ timeChipLabel(chip) }}
              </button>
            </div>
            <div v-if="form.time === 'other'" class="mt-2">
              <TimeSlotPicker v-model="form.customTime" />
            </div>
            <div
              v-if="scheduleConflict"
              class="mt-2 flex items-start gap-2 rounded-xl border border-n-amber-6 bg-n-amber-9/10 px-3 py-2 text-[12px] text-n-amber-11"
            >
              <span
                class="i-ri-alert-line mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <span>
                {{
                  t('KANBAN.AGENDA_BOARD.SCHEDULE_CONFLICT', {
                    name:
                      scheduleConflict.displayName || scheduleConflict.title,
                    range: `${formatMinutes(scheduleConflict.startMin)} – ${formatMinutes(
                      scheduleConflict.startMin + scheduleConflict.durationMin
                    )}`,
                  })
                }}
              </span>
            </div>
            <label
              v-if="guardConflicts && scheduleConflict"
              class="mt-2 flex cursor-pointer items-center gap-2 text-[12.5px] font-bold text-n-ruby-11"
              data-testid="schedule-conflict-override"
            >
              <input
                v-model="overrideConflict"
                type="checkbox"
                class="size-4 accent-n-ruby-9"
              />
              {{ t('KANBAN.AGENDA_BOARD.OVERRIDE_CONFLICT') }}
            </label>
          </div>

          <div class="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.DURATION') }}
              </span>
              <div class="flex gap-1.5">
                <button
                  v-for="duration in DURATION_CHIPS"
                  :key="duration"
                  type="button"
                  class="h-8 flex-1 rounded-full text-[12.5px] font-bold tabular-nums"
                  :class="
                    form.duration === duration
                      ? 'bg-n-slate-12 text-n-solid-1'
                      : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
                  "
                  @click="form.duration = duration"
                >
                  {{ durationLabel(duration) }}
                </button>
              </div>
            </div>
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.RESPONSIBLE') }}
              </span>
              <select
                v-model="form.responsibleId"
                data-testid="schedule-responsible"
                :aria-label="t('KANBAN.AGENDA_BOARD.RESPONSIBLE')"
                class="!h-8 !mb-0 w-full rounded-xl border border-n-weak bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12"
              >
                <option :value="null" disabled>
                  {{ t('KANBAN.AGENDA_BOARD.PICK_RESPONSIBLE') }}
                </option>
                <option
                  v-for="agent in responsibleOptions"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Conversa sem cartão: o aviso no WhatsApp não pode ser oferecido
               (prévia e envio são endpoints do cartão). A tela diz o motivo em
               vez de esconder, e a ligação fica logo abaixo. -->
          <p
            v-if="showWahaNeedsCard"
            class="m-0 flex items-start gap-1.5 text-[11.5px] leading-snug text-n-slate-10"
            data-testid="schedule-needs-card"
          >
            <span
              class="i-ri-information-line mt-px size-3.5 shrink-0"
              aria-hidden="true"
            />
            {{ t('KANBAN.AGENDA_BOARD.LINK_CARD_FOR_NOTICE') }}
          </p>

          <!-- Linha opcional: ligar o compromisso da conversa a um cartão. -->
          <div v-if="showCardLink && cardOptions.length">
            <label
              for="agenda-card-link"
              class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              {{ t('KANBAN.AGENDA_BOARD.LINK_CARD') }}
            </label>
            <select
              id="agenda-card-link"
              v-model="form.linkedCardId"
              data-testid="schedule-card-link"
              class="!h-9 !mb-0 w-full rounded-xl border border-n-weak bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12"
            >
              <option :value="null">
                {{ t('KANBAN.AGENDA_BOARD.LINK_CARD_NONE') }}
              </option>
              <option
                v-for="card in cardOptions"
                :key="card.id"
                :value="card.id"
              >
                {{ card.title }}
              </option>
            </select>
          </div>

          <!-- Uma linha recolhida para tudo que é opcional. -->
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-xl px-1 py-1.5 text-left text-[12.5px] font-bold text-n-blue-11 hover:bg-n-alpha-2"
            data-testid="schedule-advanced-toggle"
            :aria-expanded="advancedOpen"
            @click="advancedOpen = !advancedOpen"
          >
            <span
              class="size-4 shrink-0"
              :class="advancedOpen ? 'i-ri-subtract-line' : 'i-ri-add-line'"
              aria-hidden="true"
            />
            {{ t('KANBAN.AGENDA_BOARD.MORE_OPTIONS') }}
          </button>

          <!-- v-show (e nao v-if) para o conteudo recolhido continuar montado:
               a lista de agendas de destino carrega junto com o formulario. -->
          <div
            v-show="advancedOpen"
            class="flex flex-col gap-3"
            data-testid="schedule-advanced"
          >
            <div>
              <span
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.LOCATION') }}
              </span>
              <input
                v-model="form.location"
                :aria-label="t('KANBAN.AGENDA_BOARD.LOCATION')"
                type="text"
                :placeholder="t('KANBAN.AGENDA_BOARD.LOCATION_PLACEHOLDER')"
                class="!h-9 !mb-0 !px-2.5 !py-0 w-full rounded-xl border border-n-weak bg-n-solid-2 text-[13px] font-medium text-n-slate-12"
                @keydown.enter.prevent
              />
            </div>

            <div
              v-if="showDestinationSelect && connectionsStatus === 'error'"
              class="flex flex-wrap items-center gap-2"
            >
              <p role="alert" class="m-0 text-[12.5px] text-n-ruby-11">
                {{ t('KANBAN.AGENDA_BOARD.DESTINATION_ERROR') }}
              </p>
              <button
                type="button"
                class="h-8 rounded-full px-3 text-[12.5px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
                data-testid="agenda-destination-retry"
                @click="loadConnections()"
              >
                {{ t('KANBAN.AGENDA_BOARD.DESTINATION_RETRY') }}
              </button>
            </div>
            <div v-if="showDestinationSelect && showDestinationPicker">
              <label
                for="agenda-destination"
                class="mb-1.5 block text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.AGENDA_BOARD.DESTINATION') }}
              </label>
              <select
                id="agenda-destination"
                v-model="form.destinationId"
                data-testid="agenda-destination"
                class="!h-9 !mb-0 w-full rounded-xl border bg-n-solid-2 px-2.5 text-[13px] font-medium text-n-slate-12"
                :class="
                  destinationUnavailable ? 'border-n-amber-9' : 'border-n-weak'
                "
              >
                <option :value="CALENDAR_DESTINATION_DEFAULT">
                  {{ defaultDestinationLabel }}
                </option>
                <option
                  v-if="destinationUnavailable"
                  :value="CALENDAR_DESTINATION_UNAVAILABLE"
                  disabled
                >
                  {{ t('KANBAN.AGENDA_BOARD.DESTINATION_UNAVAILABLE') }}
                </option>
                <option
                  v-for="connection in writableDestinations"
                  :key="connection.id"
                  :value="String(connection.id)"
                >
                  {{ destinationOptionLabel(connection) }}
                </option>
              </select>
              <span
                v-if="destinationUnavailable"
                class="mt-1 block text-[11px] text-n-amber-11"
              >
                {{ t('KANBAN.AGENDA_BOARD.DESTINATION_UNAVAILABLE') }}
              </span>
            </div>
            <!-- A verdade sobre o Google fica colada no controle que a muda. -->
            <p
              v-if="googleDestinationLine"
              class="m-0 flex items-start gap-1.5 text-[11.5px] leading-snug text-n-slate-10"
              data-testid="schedule-google-destination-form"
            >
              <span
                class="mt-px size-3.5 shrink-0"
                :class="
                  googleDestinationGoes
                    ? 'i-ri-calendar-check-line text-n-blue-11'
                    : 'i-ri-calendar-line'
                "
                aria-hidden="true"
              />
              {{ googleDestinationLine }}
            </p>
            <label
              v-if="showWahaToggle"
              class="flex shrink-0 cursor-pointer items-center justify-between rounded-xl border border-n-weak px-3.5 py-2.5"
            >
              <span
                class="flex items-center gap-2 text-[13px] font-bold text-n-slate-12"
              >
                <span
                  class="i-ri-whatsapp-line size-4.5 text-n-teal-11"
                  aria-hidden="true"
                />
                {{ t('KANBAN.AGENDA_BOARD.SEND_WHATSAPP') }}
              </span>
              <input
                v-model="form.sendWhats"
                type="checkbox"
                class="size-4"
                data-testid="schedule-send-whats"
              />
            </label>

            <p
              v-if="isEditing && originDestinationLabel"
              class="m-0 text-[12px] text-n-slate-11"
            >
              {{ originSummary }}
            </p>
          </div>
        </div>

        <!-- Etapa 2: conferir e avisar -->
        <div
          v-else
          class="mt-4 flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto pr-1"
          data-testid="schedule-review"
        >
          <div
            v-if="!previewEnabled"
            class="rounded-xl border border-n-amber-9/35 bg-n-amber-9/10 px-3 py-2.5 text-[12px] text-n-amber-11"
          >
            {{ t('KANBAN.APPOINTMENT_MODAL.DISABLED_WARN') }}
          </div>

          <div
            class="flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-2 px-3.5 py-2.5"
          >
            <span
              class="flex items-center gap-2 text-[13px] font-bold tabular-nums text-n-slate-12"
            >
              <span
                class="i-ri-calendar-event-line size-4 text-n-blue-11"
                aria-hidden="true"
              />
              {{ summaryLabel }}
            </span>
            <button
              type="button"
              class="text-[12.5px] font-bold text-n-blue-11 hover:underline"
              @click="backToForm"
            >
              {{ t('KANBAN.APPOINTMENT_MODAL.CHANGE_TIME') }}
            </button>
          </div>

          <div>
            <span
              class="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10"
            >
              <span
                class="i-ri-whatsapp-line size-3.5 text-n-teal-11"
                aria-hidden="true"
              />
              {{ t('KANBAN.APPOINTMENT_MODAL.PREVIEW_LABEL') }}
            </span>
            <div
              v-if="previewLoading"
              class="space-y-1.5 rounded-2xl rounded-bl-md border border-n-weak bg-n-solid-2 p-3"
              data-testid="schedule-preview-loading"
            >
              <div class="h-3 w-3/4 animate-pulse rounded bg-n-alpha-2" />
              <div class="h-3 w-full animate-pulse rounded bg-n-alpha-2" />
            </div>
            <div
              v-else-if="previewFailed"
              class="rounded-xl border border-n-ruby-9/30 bg-n-ruby-9/10 px-3.5 py-3"
              data-testid="schedule-preview-error"
            >
              <p
                class="m-0 flex items-center gap-2 text-[13px] font-bold text-n-ruby-11"
              >
                <span
                  class="i-ri-error-warning-line size-4 shrink-0"
                  aria-hidden="true"
                />
                {{ t('KANBAN.APPOINTMENT_MODAL.PREVIEW_ERROR') }}
              </p>
              <p
                v-if="previewError"
                class="m-0 mt-1 text-[11.5px] text-n-slate-10"
              >
                {{ previewError }}
              </p>
              <button
                type="button"
                class="mt-2 h-8 rounded-full border border-n-ruby-9/40 px-3 text-[12.5px] font-bold text-n-ruby-11 hover:bg-n-ruby-9/10"
                @click="refreshPreview"
              >
                {{ t('KANBAN.APPOINTMENT_MODAL.RETRY') }}
              </button>
            </div>
            <div
              v-else
              class="whitespace-pre-wrap rounded-2xl rounded-bl-md border border-n-teal-9/30 bg-n-teal-9/10 p-3 text-[13px] leading-relaxed text-n-slate-12"
              data-testid="schedule-preview"
            >
              {{
                previewMessage || t('KANBAN.APPOINTMENT_MODAL.PREVIEW_EMPTY')
              }}
            </div>
          </div>

          <!-- Somente leitura, de proposito. Nao ha opt-out de Google no
               backend (PushEventJob publica pela conexao do funil mesmo sem
               destino explicito), e o lembrete de 30 minutos e cron global sem
               configuracao por compromisso. Chave aqui seria enfeite. A escolha
               de agenda continua sendo o select "Agenda de destino". -->
          <p
            v-if="googleDestinationLine"
            class="m-0 flex items-start gap-2 rounded-xl border border-n-weak bg-n-solid-2 px-3.5 py-2.5 text-[12.5px] leading-snug text-n-slate-11"
            data-testid="schedule-google-destination"
          >
            <span
              class="mt-0.5 size-4 shrink-0"
              :class="
                googleDestinationGoes
                  ? 'i-ri-calendar-check-line text-n-blue-11'
                  : 'i-ri-calendar-line'
              "
              aria-hidden="true"
            />
            {{ googleDestinationLine }}
          </p>
        </div>

        <p
          v-if="isWorkspaceV2 && submitted && !scheduleReady"
          role="alert"
          class="mt-3 text-sm text-n-ruby-11"
        >
          {{
            isCard
              ? t('KANBAN.AGENDA_BOARD.CARD_SCHEDULE_REQUIRED')
              : t('KANBAN.AGENDA_BOARD.SCHEDULE_REQUIRED')
          }}
        </p>
        <p
          v-if="isWorkspaceV2 && saveError"
          role="alert"
          class="mt-3 text-sm text-n-ruby-11"
        >
          {{ saveError }}
        </p>
        <div
          v-if="discardRequested"
          class="mt-3 rounded-xl border border-n-amber-6 bg-n-solid-2 p-3"
          role="alert"
        >
          <p class="text-sm text-n-slate-12">
            {{ t('KANBAN.AGENDA_BOARD.DISCARD_CHANGES') }}
          </p>
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="discardRequested = false"
            >
              {{ t('KANBAN.AGENDA_BOARD.BACK') }}
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-ruby-9/10"
              @click="emit('update:modelValue', false)"
            >
              {{ t('KANBAN.AGENDA_BOARD.DISCARD') }}
            </button>
          </div>
        </div>

        <div
          v-if="step === 'form'"
          class="mt-5 flex shrink-0 flex-wrap justify-end gap-2"
        >
          <button
            type="button"
            class="h-9 rounded-full px-4 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
            @click="close"
          >
            {{ t('KANBAN.AGENDA_BOARD.CLOSE') }}
          </button>
          <button
            type="button"
            class="h-9 rounded-full bg-n-blue-9 px-5 text-[13px] font-bold text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="schedule-primary"
            :disabled="saving || (submitted && !scheduleReady) || blocked"
            @click="primaryAction"
          >
            {{
              needsReview
                ? t('KANBAN.APPOINTMENT_MODAL.CONTINUE')
                : isRescheduling
                  ? t('KANBAN.AGENDA_BOARD.SAVE')
                  : t('KANBAN.AGENDA_BOARD.CONFIRM_SCHEDULE')
            }}
          </button>
        </div>
        <div v-else class="mt-5 flex shrink-0 flex-wrap justify-end gap-2">
          <button
            type="button"
            class="h-9 rounded-full px-4 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
            @click="backToForm"
          >
            {{ t('KANBAN.AGENDA_BOARD.BACK') }}
          </button>
          <button
            type="button"
            class="h-9 rounded-full border border-n-weak px-4 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="schedule-save-only"
            :disabled="saving"
            @click="saveWithoutNotice"
          >
            {{ t('KANBAN.APPOINTMENT_MODAL.SAVE_ONLY') }}
          </button>
          <button
            type="button"
            class="h-9 rounded-full bg-n-blue-9 px-5 text-[13px] font-bold text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="schedule-save-notify"
            :disabled="
              saving || previewLoading || previewFailed || !previewEnabled
            "
            @click="emitSubmit"
          >
            {{ t('KANBAN.AGENDA_BOARD.SAVE_AND_NOTIFY') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
